import { useEffect, useState } from "react";
import Prompt, { PromptRating, PromptStatus } from "@/models/prompt/prompt";
import usePrompts from "@/hooks/usePrompts";
import { User } from "@/models/users/User";
import useUsers from "@/hooks/useUsers";
import { useAuth0 } from "@auth0/auth0-react";
import useTags from "@/hooks/useTags";
import Tag from "@/models/tag/tag";
import { PromptSchemaType } from "../components/prompt-library/PromptLibraryForm";
import {
  showSuccessToast,
  showErrorToast,
} from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";

interface UsePromptLibraryReturn {
  myUser: User | undefined;
  tags: Tag[];
  crudPending: boolean;
  handleRatePrompt: (promptId: string, rating: PromptRating) => Promise<void>;
  handleUsePrompt: (prompt: Prompt) => void;
  handleCreatePrompt: (values: PromptSchemaType) => Promise<boolean>;
  handleUpdatePrompt: (
    promptId: string,
    values: PromptSchemaType
  ) => Promise<boolean>;
  handleDeletePrompt: (promptId: string) => Promise<void>;
  handlePromptStatusToPending: (promptId: string) => Promise<void>;
  handleFavoritePrompt: (promptId: string) => Promise<void>;
}

const usePromptLibrary = (): UsePromptLibraryReturn => {
  // Hooks
  const {
    ratePrompt,
    usePrompt,
    create,
    update,
    deletePrompt,
    updateStatus,
    favoritePrompt,
  } = usePrompts();
  const { useQueryPagination: tagPagination } = useTags();
  const { useQueryAllTenantUsers } = useUsers();
  const { user } = useAuth0();

  // Data state
  const [users, setUsers] = useState<User[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [myUser, setMyUser] = useState<User | undefined>(undefined);

  const fetchedTags = tagPagination({
    pageNumber: 1,
    pageSize: 1000000,
    sortAscending: true,
  });

  const fetchedUsers = useQueryAllTenantUsers();

  useEffect(() => {
    if (fetchedUsers.isFetched && fetchedUsers.data) {
      setUsers(fetchedUsers.data);
    }
  }, [fetchedUsers.data, fetchedUsers.isFetched]);

  useEffect(() => {
    if (fetchedTags.isFetched && fetchedTags.data && fetchedTags.data.data) {
      setTags(fetchedTags.data.data);
    }
  }, [fetchedTags.data, fetchedTags.isFetched]);

  useEffect(() => {
    if (users.length && user) {
      setMyUser(users.find((u) => u.email === user.email));
    }
  }, [users, user]);

  const handleRatePrompt = async (promptId: string, rating: PromptRating) => {
    await ratePrompt.mutateAsync({ id: promptId, rating });
  };

  const handleUsePrompt = (prompt: Prompt) => {
    usePrompt.mutate(prompt.id);
  };

  const handleCreatePrompt = async (values: PromptSchemaType) => {
    try {
      const promptToCreate: Partial<Prompt> = {
        promptString: values.promptString,
        tags: values.tags as Tag[],
        description: values.description,
        title: values.title,
        status: values.status,
      };

      await create.mutateAsync(promptToCreate);

      showSuccessToast("Prompten er blevet oprettet");

      return true;
    } catch (error) {
      logger.error("Failed to create prompt", {
        operation: "createPrompt",
        error,
      });
      showErrorToast("Der skete en fejl ved oprettelse af prompten");

      return false;
    }
  };

  const handleUpdatePrompt = async (
    promptId: string,
    values: PromptSchemaType
  ) => {
    try {
      const promptToUpdate: Partial<Prompt> = {
        promptString: values.promptString,
        tags: values.tags as Tag[],
        description: values.description,
        title: values.title,
        status: values.status,
      };

      await update.mutateAsync({ id: promptId, ...promptToUpdate });

      showSuccessToast("Prompten er blevet opdateret");

      return true;
    } catch (error) {
      logger.error("Failed to update prompt", {
        operation: "updatePrompt",
        error,
        promptId,
      });
      showErrorToast("Der skete en fejl ved opdatering af prompten");
      return false;
    }
  };

  const handleDeletePrompt = async (promptId: string) => {
    await deletePrompt.mutateAsync(promptId);
  };

  const handlePromptStatusToPending = async (promptId: string) => {
    await updateStatus.mutateAsync({
      id: promptId,
      status: PromptStatus.Pending,
    });
  };

  const handleFavoritePrompt = async (promptId: string) => {
    await favoritePrompt.mutateAsync(promptId);
  };

  return {
    myUser,
    tags,
    crudPending: create.isPending || update.isPending || deletePrompt.isPending,
    handleRatePrompt,
    handleUsePrompt,
    handleCreatePrompt,
    handleUpdatePrompt,
    handleDeletePrompt,
    handlePromptStatusToPending,
    handleFavoritePrompt,
  };
};

export default usePromptLibrary;
